import React from 'react';
import {OrderType, PlaceApi} from "../../../../../../../services/Api/types";
import OrderTypesFilterButton from "./OrderTypesFilterButton";
import {usePlaceContext} from "../../../../../../../services/Place/PlaceContext";
import ScrollableTabs from "../../../../../../../components/Common/ScrollableTabs";
import {connect} from "react-redux";
import {RootState} from "../../../../../../../../reducers";
import {withTranslation} from "react-i18next";
import {getSession} from "../../../../../../../services/Session/reducer";
import {calcPlaceDistance, getSortedPlacesByDistance} from "../../../../../utils/multistoreUtils";

interface OwnProps {
    setSelectedOrderType: React.Dispatch<React.SetStateAction<OrderType | undefined>>;
    selectedOrderType: OrderType | undefined;
    setShowAddressModal: React.Dispatch<React.SetStateAction<boolean>>;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const OrderTypesFilter = ({setSelectedOrderType, selectedOrderType, session, setShowAddressModal}: Props) => {
    const {places} = usePlaceContext();

    const anyPlaceSupportsOrderType = (orderType: OrderType) => {
        return (places || []).some(place => place.types.includes(orderType));
    }

    const getOrderTypes = () => {
        const orderTypes: OrderType[] = [];

        if (anyPlaceSupportsOrderType(OrderType.DELIVERY)) orderTypes.push(OrderType.DELIVERY);
        if (anyPlaceSupportsOrderType(OrderType.PICK_UP)) orderTypes.push(OrderType.PICK_UP);
        if (anyPlaceSupportsOrderType(OrderType.DINE_IN)) orderTypes.push(OrderType.DINE_IN);
        if (anyPlaceSupportsOrderType(OrderType.ROOM_SERVICE)) orderTypes.push(OrderType.ROOM_SERVICE);

        return orderTypes;
    }

    const handleClick = (orderType: OrderType, event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();

        if (selectedOrderType === orderType) {
            setSelectedOrderType(undefined);
        } else {
            setSelectedOrderType(orderType);

            if (orderType === OrderType.DELIVERY && !session.full_address) {
                setShowAddressModal(true);
            }
        }
        event.currentTarget.blur();
    };

    const orderTypes = getOrderTypes();

    if (orderTypes.length === 0) return <></>;

    const getInfoPlacesForOrderType = (orderType: OrderType): {
        placesNumber: number,
        nearestPlaceDistance: number | undefined,
        outsideDeliveryZone: boolean
    } => {
        let filteredPlaces: PlaceApi[] = [];

        if (orderType === OrderType.DELIVERY) {
            filteredPlaces = [...(places || []).filter(place => session.full_address ?
                (place.in_delivery_zone && place.types.includes(OrderType.DELIVERY))
                : place.types.includes(OrderType.DELIVERY)
            )];
        } else {
            filteredPlaces = [...(places || []).filter(place => place.types.includes(orderType))]
        }

        const sortedPlaces = getSortedPlacesByDistance(filteredPlaces);

        return {
            placesNumber: sortedPlaces.length,
            nearestPlaceDistance: sortedPlaces.length > 0 ? calcPlaceDistance(sortedPlaces[0]) : undefined,
            outsideDeliveryZone: sortedPlaces.filter(place => !place.in_delivery_zone).length === sortedPlaces.length
        }
    }

    return (
        <ScrollableTabs className="multistore-order-types-filter">
            {
                orderTypes.map(orderType => (
                    <OrderTypesFilterButton orderType={orderType} key={orderType}
                                            placeInfo={getInfoPlacesForOrderType(orderType)}
                                            selected={selectedOrderType === orderType}
                                            session={session}
                                            onClick={(event) => handleClick(orderType, event)}/>))
            }
        </ScrollableTabs>
    )
}

const mapStateToProps = (state: RootState) => ({
    session: getSession(state.session),
});

const connector = connect(mapStateToProps);
export default withTranslation()(connector(OrderTypesFilter));